import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { getColDefs } from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/SalaryMass/helpers/colDefinitions';
import { DateVentilation } from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/helpers/hrVizInfos';
import {
    formatSalaryMassTableIndicatorsForBanner,
    isAllUnmapped,
} from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/SalaryMass/helpers/dataHelpers';
import {
    DateString,
    SalaryMassLine,
} from 'routes/HumanResourcesModule/business/types';
import { StyHrDataGrid } from 'routes/HumanResourcesModule/components/HrDataGrid/HrDataGrid';
import NoData from 'routes/HumanResourcesModule/components/HrDataGrid/NoData';
import { getExerciceMonthlyPeriod } from 'routes/HumanResourcesModule/PayState/helpers/dataHelpers';
import PayStateBanner from '../../components/PayStateBanner';
import Loading from 'components/Loading';
import { PayStateT } from 'routes/HumanResourcesModule/PayState/helpers/types';
import { useGetTableSalaryMassIndicators } from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/helpers/hooks/payStateIndicators';
import {
    NON_PERMANENT,
    PERMANENT,
} from 'routes/HumanResourcesModule/business/definitions';
import { TRACKABLE_EVENTS_METADATA } from 'helpers/trackerHelpers';

type Props = {
    payState: PayStateT;
    title: string;
    tooltip?: string;
    isForPermanentAgent: boolean;
    cardHeaderActions: React.ReactNode;
    salaryMass: SalaryMassLine[];
    dateColumns: DateString[];
    isLoading: boolean;
    dateVentilation: DateVentilation;
    showLastYearColumns: boolean;
};

const SalaryMassGrid = ({
    payState,
    title,
    tooltip,
    isForPermanentAgent,
    cardHeaderActions,
    salaryMass,
    dateColumns,
    isLoading,
    dateVentilation,
    showLastYearColumns,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const isMonthly = dateVentilation === DateVentilation.MONTHLY;

    const { data: indicators, isLoading: indicatorsLoading } =
        useGetTableSalaryMassIndicators(payState.id, isForPermanentAgent);

    const fmtIndicator = formatSalaryMassTableIndicatorsForBanner(
        indicators,
        isForPermanentAgent,
    );

    const shouldDisplayPermanent = isAllUnmapped(salaryMass);

    const columnDefs = getColDefs({
        dateColumns,
        isMonthly,
        exercice: payState.exercice,
        isForPermanentAgent,
        shouldDisplayPermanent,
        showLastYearColumns,
        t,
    });

    const exportFileName = t(
        'humanResources.payState.salaryMassExportFileName',
        {
            dateVentilation: t(
                `humanResources.${dateVentilation}`,
            ).toLowerCase(),
        },
    );

    const bannerComponent = ({ children }: { children: React.ReactElement }) =>
        indicatorsLoading ? (
            <Loading />
        ) : (
            <PayStateBanner
                title={t(
                    `humanResources.payState.indicators.${isForPermanentAgent ? PERMANENT : NON_PERMANENT}`,
                )}
                exercice={payState.exercice}
                data={fmtIndicator}
            >
                {children}
            </PayStateBanner>
        );

    return (
        <StyHrDataGrid
            title={title}
            trackEventMetadata={{
                gridName: isForPermanentAgent
                    ? TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                          .permanentSalaryMassGrid
                    : TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                          .nonPermanentSalaryMassGrid,
            }}
            infoTooltip={tooltip}
            rowData={salaryMass}
            columnDefs={columnDefs}
            exportFileName={exportFileName}
            isLoading={isLoading}
            cardHeaderActions={cardHeaderActions}
            noDataNode={
                <NoData
                    message={t('humanResources.payState.noDataPeriod', {
                        year: payState.exercice,
                        ...getExerciceMonthlyPeriod(payState.exercice),
                    })}
                />
            }
            wrapperComponent={bannerComponent}
        />
    );
};

export default memo(SalaryMassGrid);
